import { createFileRoute, redirect, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { LayoutDashboard, Building2, CreditCard, Activity, ServerCog, BookOpen, LogOut, Layers } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useAuth } from '@/lib/auth-store'
import { adminApi } from '@/lib/api'
import { Badge } from '@/components/ui'

export const Route = createFileRoute('/_app')({
  beforeLoad: () => {
    const state = useAuth.getState()
    if (!state.isAuthenticated()) throw redirect({ to: '/login' })
    // Solo superadmin ve el panel global. Owners → primer tenant con membership.
    if (state.mode === 'owner') {
      const first = state.ownerMemberships[0]
      if (first) throw redirect({ to: '/store/$id', params: { id: first.tenantId } })
      throw redirect({ to: '/login' })
    }
  },
  component: AppLayout,
})

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/tenants', label: 'Tiendas', icon: Building2 },
  { to: '/plans', label: 'Planes', icon: Layers },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  { to: '/activity', label: 'Actividad', icon: Activity },
  { to: '/system', label: 'Sistema', icon: ServerCog },
  { to: '/docs', label: 'Docs', icon: BookOpen },
]

function AppLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout: clearAuth } = useAuth()

  // Health ping cada 60s
  const { data: dashboardData } = useQuery({
    queryKey: ['platform-dashboard'],
    queryFn: () =>
      adminApi.get<{ data: { tenants: { total: number; active: number }; system: { healthy: boolean } } }>(
        '/api/admin/dashboard',
      ),
    refetchInterval: 60_000,
  })

  function logout() {
    clearAuth()
    navigate({ to: '/login' })
  }

  return (
    <div className="flex h-screen bg-neutral-950 text-neutral-100">
      <aside className="w-64 border-r border-neutral-800 bg-neutral-900/50 flex flex-col">
        <div className="px-6 py-5 border-b border-neutral-800">
          <h1 className="text-lg font-bold tracking-tight">Core Platform</h1>
          <p className="text-xs text-neutral-500 mt-0.5">Panel de administración</p>
        </div>

        <div className="px-6 py-3 border-b border-neutral-800 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-neutral-500">Tiendas activas</span>
            <span className="font-mono text-neutral-200">
              {dashboardData ? `${dashboardData.data.tenants.active} / ${dashboardData.data.tenants.total}` : '—'}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-neutral-500">Sistema</span>
            <Badge variant={dashboardData?.data.system.healthy ? 'success' : 'destructive'}>
              {dashboardData?.data.system.healthy ? 'OK' : 'Error'}
            </Badge>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, exact }) => {
            const active = exact
              ? location.pathname === to
              : (location.pathname === to || location.pathname.startsWith(to + '/'))
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition',
                  active
                    ? 'bg-neutral-800 text-white'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50',
                )}
              >
                <Icon className="size-4" />
                {label}
              </Link>
            )
          })}
        </nav>

        <div className="p-3 border-t border-neutral-800 space-y-2">
          <Badge variant="muted" className="w-full justify-center">v0.1.0 · platform</Badge>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-neutral-400 hover:text-white hover:bg-neutral-800/50 transition"
          >
            <LogOut className="size-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  )
}
